import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Edit, Upload, Download, FileSpreadsheet, Search, Package, Loader2, ShoppingBag, Printer } from "lucide-react";
import { toast } from "sonner";
import { FORMATOS, imprimirEtiquetas } from "@/lib/etiquetas";

export default function AdminRepuestos() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const fileRef = useRef(null);
  const [repuestos, setRepuestos] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ sku: "", nombre: "", categoria: "", precio_venta: "" });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [enTienda, setEnTienda] = useState({}); // repuesto_id → producto de la tienda enlazado
  const [printOpen, setPrintOpen] = useState(false);
  const [printItems, setPrintItems] = useState([]); // repuestos a imprimir
  const [formato, setFormato] = useState("t50x25");
  const [copias, setCopias] = useState("1");

  const abrirImpresion = (items) => {
    const conSku = items.filter((r) => (r.sku || "").trim());
    if (conSku.length === 0) return toast.error("No hay repuestos con SKU para imprimir");
    setPrintItems(conSku); setPrintOpen(true);
  };

  const imprimir = () => {
    const ok = imprimirEtiquetas(printItems, formato, Number(copias) || 1);
    if (!ok) toast.error("El navegador bloqueó la ventana de impresión — permite los pop-ups de odontofix.cl");
    else setPrintOpen(false);
  };

  const fmtCLP = (n) => "$" + (Number(n) || 0).toLocaleString("es-CL");

  const load = () => {
    api.get("/api/repuestos").then((r) => setRepuestos(r.data)).catch(() => {});
    api.get("/api/tienda/admin/productos").then((r) => {
      const m = {};
      r.data.forEach((p) => { if (p.repuesto_id) m[p.repuesto_id] = p; });
      setEnTienda(m);
    }).catch(() => {});
  };
  useEffect(() => { load(); seedIfEmpty(); }, []);

  const seedIfEmpty = async () => {
    try { await api.post("/api/repuestos/seed"); } catch {}
    load();
  };

  const uf = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const openNew = () => { setEditing(null); setForm({ sku: "", nombre: "", categoria: "", precio_venta: "" }); setOpen(true); };
  const openEdit = (rep) => { setEditing(rep); setForm({ sku: rep.sku, nombre: rep.nombre, categoria: rep.categoria || "", precio_venta: rep.precio_venta || "" }); setOpen(true); };

  const save = async () => {
    if (!form.sku || !form.nombre) { toast.error("SKU y Nombre son obligatorios"); return; }
    setSaving(true);
    const payload = { ...form, precio_venta: Number(form.precio_venta) || 0 };
    try {
      if (editing) {
        await api.put(`/api/repuestos/${editing.id}`, payload);
        toast.success("Repuesto actualizado");
      } else {
        await api.post("/api/repuestos", payload);
        toast.success("Repuesto creado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (rep) => {
    if (!window.confirm(`¿Eliminar "${rep.sku} — ${rep.nombre}"?`)) return;
    try { await api.delete(`/api/repuestos/${rep.id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/api/repuestos/export/template", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "modelo_repuestos_odontofix.xlsx"; a.click();
    } catch { toast.error("Error al descargar"); }
  };

  const downloadExcel = async () => {
    try {
      const r = await api.get("/api/repuestos/export/excel", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "repuestos_odontofix.xlsx"; a.click();
    } catch { toast.error("Error al descargar"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/api/repuestos/import/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      if (d.errores > 0) {
        toast.error(`${d.errores} errores en la carga`, { description: d.detalle_errores?.join(", ") });
      }
      if (d.creados > 0) toast.success(`${d.creados} repuestos importados`);
      if (d.omitidos_duplicados > 0) toast.info(`${d.omitidos_duplicados} SKUs duplicados omitidos`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const filtered = repuestos.filter((r) =>
    r.sku?.toLowerCase().includes(search.toLowerCase()) ||
    r.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    r.categoria?.toLowerCase().includes(search.toLowerCase())
  );
  const categorias = [...new Set(repuestos.map((r) => r.categoria).filter(Boolean))].sort();

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-[#0FB4B7]" />
            <span className="text-sm font-semibold text-[#384091]">Catálogo de Repuestos</span>
            <Badge className="bg-[#0FB4B7] text-white text-[10px]">{repuestos.length}</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
            <ArrowLeft className="h-4 w-4" /><span className="ml-1">Volver</span>
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Actions bar */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input placeholder="Buscar por SKU, nombre o categoría..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
          </div>
          <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={openNew}>
            <Plus className="h-4 w-4 mr-1" />Nuevo
          </Button>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-1" />Modelo Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            Importar Excel
          </Button>
          <Button variant="outline" size="sm" onClick={downloadExcel}>
            <FileSpreadsheet className="h-4 w-4 mr-1" />Exportar
          </Button>
          <Button variant="outline" size="sm" className="border-[#384091]/40 text-[#384091]"
            onClick={() => abrirImpresion(filtered)}>
            <Printer className="h-4 w-4 mr-1" />Imprimir códigos
          </Button>
        </div>

        {/* Table */}
        {categorias.map((cat) => (
          <div key={cat}>
            <h3 className="text-sm font-semibold text-[#384091] mb-2 mt-3">{cat}</h3>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-2 text-gray-500 font-medium w-28">SKU</th>
                      <th className="text-left px-4 py-2 text-gray-500 font-medium">Nombre</th>
                      <th className="text-right px-4 py-2 text-gray-500 font-medium w-32">Precio Venta</th>
                      <th className="text-right px-4 py-2 text-gray-500 font-medium w-24">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.filter((r) => r.categoria === cat).map((rep) => (
                      <tr key={rep.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-xs text-[#0FB4B7] font-bold">{rep.sku}</td>
                        <td className="px-4 py-2">
                          {rep.nombre}
                          {enTienda[rep.id] && (
                            <Badge className={`ml-2 border-0 text-[10px] ${enTienda[rep.id].activo ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                              {enTienda[rep.id].activo ? "EN TIENDA" : "OCULTO"}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-[#384091]">{rep.precio_venta ? fmtCLP(rep.precio_venta) : "—"}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button onClick={() => abrirImpresion([rep])} title="Imprimir código de barras"
                            className="p-1 text-gray-400 hover:text-[#384091]">
                            <Printer className="h-3.5 w-3.5" /></button>
                          <button onClick={() => navigate(`/tecnico/tienda?vender=${rep.id}`)}
                            title={enTienda[rep.id] ? "Ver su producto en la tienda" : "Vender en la tienda web"}
                            className={`p-1 ${enTienda[rep.id] ? "text-emerald-600 hover:text-emerald-700" : "text-gray-400 hover:text-[#E0653A]"}`}>
                            <ShoppingBag className="h-3.5 w-3.5" /></button>
                          <button onClick={() => openEdit(rep)} className="text-gray-400 hover:text-[#384091] p-1 ml-1"><Edit className="h-3.5 w-3.5" /></button>
                          <button onClick={() => remove(rep)} className="text-gray-400 hover:text-red-500 p-1 ml-1"><Trash2 className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        ))}
        {/* Uncategorized */}
        {filtered.filter((r) => !r.categoria).length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2 mt-3">Sin categoría</h3>
            <Card className="border-0 shadow-sm"><CardContent className="p-0">
              <table className="w-full text-sm"><tbody>
                {filtered.filter((r) => !r.categoria).map((rep) => (
                  <tr key={rep.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-[#0FB4B7] font-bold w-28">{rep.sku}</td>
                    <td className="px-4 py-2">
                      {rep.nombre}
                      {enTienda[rep.id] && (
                        <Badge className={`ml-2 border-0 text-[10px] ${enTienda[rep.id].activo ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {enTienda[rep.id].activo ? "EN TIENDA" : "OCULTO"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-[#384091] w-32">{rep.precio_venta ? fmtCLP(rep.precio_venta) : "—"}</td>
                    <td className="px-4 py-2 text-right w-24 whitespace-nowrap">
                      <button onClick={() => abrirImpresion([rep])} title="Imprimir código de barras"
                        className="p-1 text-gray-400 hover:text-[#384091]">
                        <Printer className="h-3.5 w-3.5" /></button>
                      <button onClick={() => navigate(`/tecnico/tienda?vender=${rep.id}`)}
                        title={enTienda[rep.id] ? "Ver su producto en la tienda" : "Vender en la tienda web"}
                        className={`p-1 ${enTienda[rep.id] ? "text-emerald-600 hover:text-emerald-700" : "text-gray-400 hover:text-[#E0653A]"}`}>
                        <ShoppingBag className="h-3.5 w-3.5" /></button>
                      <button onClick={() => openEdit(rep)} className="text-gray-400 hover:text-[#384091] p-1 ml-1"><Edit className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(rep)} className="text-gray-400 hover:text-red-500 p-1 ml-1"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            </CardContent></Card>
          </div>
        )}
      </div>

      {/* Diálogo de impresión de códigos de barra */}
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Imprimir códigos de barra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Se imprimirá(n) <b>{printItems.length}</b> etiqueta(s) con nombre, SKU y código de
              barras CODE128 (pistoleable). En la ventana de impresión eliges tu impresora y papel.
            </p>
            <div><Label>Formato</Label>
              <Select value={formato} onValueChange={setFormato}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {FORMATOS.map((f) => <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label>Copias por repuesto</Label>
              <Input type="number" min="1" max="50" value={copias}
                onChange={(e) => setCopias(e.target.value.replace(/\D/g, ""))}
                className="mt-1 bg-white w-28" /></div>
            <Button className="w-full bg-[#384091] hover:bg-[#2D3375] text-white" onClick={imprimir}>
              <Printer className="h-4 w-4 mr-1" /> Imprimir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} repuesto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>SKU *</Label><Input value={form.sku} onChange={(e) => uf("sku", e.target.value.toUpperCase())} placeholder="EJ: SUC-001" className="mt-1 bg-white font-mono" /></div>
            <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => uf("nombre", e.target.value)} placeholder="Nombre del repuesto" className="mt-1 bg-white" /></div>
            <div><Label>Categoría</Label><Input value={form.categoria} onChange={(e) => uf("categoria", e.target.value)} placeholder="Ej: Succión, Neumática, Eléctrica..." className="mt-1 bg-white" /></div>
            <div>
              <Label>Precio de venta (CLP)</Label>
              <Input type="number" min="0" value={form.precio_venta} onChange={(e) => uf("precio_venta", e.target.value.replace(/\D/g, ""))} placeholder="Ej: 15000" className="mt-1 bg-white" />
              <p className="text-[11px] text-gray-400 mt-1">Si el repuesto está publicado en la tienda web, este precio se sincroniza con ella (y viceversa).</p>
            </div>
            <Button className="w-full bg-[#384091] hover:bg-[#2D3375] text-white" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editing ? "Guardar cambios" : "Crear repuesto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
